import React from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "../lib/utils";
import { BookOpen, Users, Calendar, Award, Menu, X, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ui/theme-toggle";

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navigationItems = [
    { name: "Home", path: createPageUrl("Home"), icon: BookOpen },
    { name: "Tutors", path: createPageUrl("Tutors"), icon: Users },
    { name: "Bookings", path: createPageUrl("Bookings"), icon: Calendar },
    { name: "Apply", path: createPageUrl("Apply"), icon: Calendar },
    { name: "Our Team", path: createPageUrl("Team"), icon: Award },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 
      dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 
      transition-colors duration-300">

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-lg 
        border-b border-gray-100 dark:border-zinc-800 shadow-sm transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to={createPageUrl("Home")} className="flex items-center gap-3 group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg 
                group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                    <img 
                      src="/logo192.png" 
                      alt="Zenith Academics Logo"
                      className="w-full h-full object-cover"
                    />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 
                  dark:from-red-400 dark:via-orange-400 dark:to-amber-400 bg-clip-text text-transparent">
                  Zenith Academics
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Empowering Student Success
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.name} to={item.path}>
                    <Button
                      variant="ghost"
                      className={`gap-2 transition-all duration-200 ${
                        isActive
                          ? "bg-blue-50 dark:bg-red-900/50 text-blue-700 dark:text-red-300 font-semibold"
                          : "text-gray-600 dark:text-zinc-300 hover:text-blue-700 dark:hover:text-red-400 hover:bg-blue-50/50 dark:hover:bg-red-900/30"
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
              
              <div className="ml-2">
                <ThemeToggle />
              </div>
            </div>

            {/* Mobile Menu Button & Theme Toggle */}
            <div className="md:hidden flex items-center gap-3">
              <ThemeToggle />
              
              <button
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700 dark:text-zinc-300" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700 dark:text-zinc-300" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 dark:border-zinc-800 
            bg-white/95 dark:bg-zinc-900/95 backdrop-blur-lg transition-colors">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.name} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start gap-3 ${
                        isActive
                          ? "bg-blue-50 dark:bg-red-900/50 text-blue-700 dark:text-red-300 font-semibold"
                          : "text-gray-600 dark:text-zinc-300 hover:text-blue-700 dark:hover:text-red-400 hover:bg-blue-50/50 dark:hover:bg-red-900/30"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 to-blue-900 
        dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 text-white mt-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg 
                  group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                      <img 
                        src="/logo192.png" 
                        alt="Zenith Academics Logo"
                        className="w-full h-full object-cover"
                      />
                </div>
                <h3 className="text-xl font-bold">Zenith Academics</h3>
              </div>
              <p className="text-blue-100 dark:text-zinc-300">
                Affordable, high-quality tutoring by top students for middle and high schoolers.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-lg">Quick Links</h4>
              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block text-blue-100 dark:text-zinc-300 hover:text-white transition-colors"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Get Started */}
            <div>
              <h4 className="font-semibold mb-4 text-lg">Get Started</h4>
              <p className="text-blue-100 dark:text-zinc-300 mb-4">
                Ready to transform your learning experience?
              </p>
              <Link to={createPageUrl("Bookings")}>
                <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 dark:from-amber-500 dark:to-orange-500 dark:hover:from-amber-600 dark:hover:to-orange-600 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                  Book a Session
                </Button>
              </Link>
              <Link to={createPageUrl("Apply")}>
                <Button variant="outline" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 dark:from-amber-500 dark:to-orange-500 dark:hover:from-amber-600 dark:hover:to-orange-600 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                  Become a Tutor
                </Button>
              </Link>
            </div>

            {/* Contact Us */}
            <div>
              <h4 className="font-semibold mb-4 text-lg">Need Help?</h4>
              <p className="text-blue-100 dark:text-zinc-300 mb-6">
                Have questions? We're here to help you succeed.
              </p>
              <Link to={createPageUrl("contactus")}>
                <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 dark:from-amber-500 dark:to-orange-500 dark:hover:from-amber-600 dark:hover:to-orange-600 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/10 dark:border-zinc-800 mt-8 pt-8 text-center text-blue-100 dark:text-zinc-300">
            <p>&copy; {new Date().getFullYear()} Zenith Academics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}